import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageUploader from "@/components/admin/ImageUploader";
import { api, formatApiError } from "@/lib/api";

const SETS = {
  fleet: {
    endpoint: "fleet-categories",
    label: "Fleet Categories",
    empty: { name: "", slug: "", description: "", typical_freight: "", image_url: "", order: 0, active: true },
    imageField: "image_url",
    imageKind: "fleet",
    imageLabel: "Fleet Category Photo",
    fields: [
      { key: "name", label: "Name" },
      { key: "slug", label: "Slug (kebab-case)" },
      { key: "description", label: "Description", textarea: true },
      { key: "typical_freight", label: "Typical Freight" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  services: {
    endpoint: "services",
    label: "Services",
    empty: { name: "", slug: "", description: "", icon: "truck", order: 0, active: true },
    fields: [
      { key: "name", label: "Name" },
      { key: "slug", label: "Slug" },
      { key: "description", label: "Description", textarea: true },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  areas: {
    endpoint: "service-areas",
    label: "Service Areas",
    empty: { name: "", kind: "state", notes: "", order: 0, active: true },
    fields: [
      { key: "name", label: "Name" },
      { key: "kind", label: "Kind (state/region/city)" },
      { key: "notes", label: "Notes", textarea: true },
      { key: "order", label: "Order", type: "number" },
    ],
  },
};

function CrudBlock({ cfg }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(cfg.empty);

  const load = useCallback(() => {
    api.get(`/${cfg.endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  }, [cfg.endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(cfg.empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...cfg.empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      for (const f of cfg.fields) if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (editing) await api.put(`/${cfg.endpoint}/${editing.id}`, payload);
      else await api.post(`/${cfg.endpoint}`, payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/${cfg.endpoint}/${id}`); load(); };
  const toggle = async (it) => { await api.put(`/${cfg.endpoint}/${it.id}`, { ...it, active: !it.active }); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-slate-400 text-sm">{items.length} item(s)</div>
        <Button onClick={openNew} className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-10 font-head uppercase tracking-widest text-xs"><Plus className="h-4 w-4 mr-2" /> New</Button>
      </div>
      <div className="border border-[var(--lt-border)] bg-[var(--lt-panel)] divide-y divide-[var(--lt-border)]">
        {items.length === 0 && <div className="p-6 text-slate-500 text-sm text-center">Empty.</div>}
        {items.map((it) => (
          <div key={it.id} className="p-5 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-head uppercase tracking-widest text-white text-sm">{it.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{it.description || it.notes || ""}</div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={it.active} onCheckedChange={() => toggle(it)} />
              <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="text-slate-400 hover:text-white"><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--lt-panel)] border-[var(--lt-border)] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase">{editing ? `Edit ${cfg.label}` : `New ${cfg.label}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {cfg.imageField && (
              <ImageUploader
                label={cfg.imageLabel || "Image"}
                value={form[cfg.imageField] || ""}
                onChange={(url) => setForm({ ...form, [cfg.imageField]: url })}
                kind={cfg.imageKind || "image"}
                testid={`crud-${cfg.endpoint}-image`}
                help="This photo shows up wherever this record is rendered on the site."
                aspect="16 / 9"
              />
            )}
            {cfg.fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs font-mono-lt uppercase tracking-[0.2em] text-slate-400 mb-2 block">{f.label}</Label>
                {f.textarea
                  ? <Textarea rows={3} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white rounded-xl" />
                  : <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-[var(--lt-bg)] border-[var(--lt-border)] text-white h-11 rounded-xl" />}
              </div>
            ))}
            <div className="flex items-center gap-3"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm text-slate-300">Active</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-[var(--lt-border)] bg-transparent text-slate-300">Cancel</Button>
            <Button onClick={save} className="rounded-xl bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white font-head uppercase tracking-widest">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ContentManager() {
  return (
    <div>
      <div className="font-mono-lt text-xs uppercase tracking-[0.3em] text-[var(--lt-accent-bright)] mb-2">// Website Content</div>
      <h1 className="font-display font-black text-4xl text-white uppercase mb-8">Content Manager</h1>

      <Tabs defaultValue="fleet">
        <TabsList className="bg-[var(--lt-bg)] border border-[var(--lt-border)] rounded-xl h-11 p-0">
          <TabsTrigger value="fleet" className="rounded-xl h-11 px-6 font-head uppercase tracking-widest text-xs data-[state=active]:bg-[var(--lt-accent)] data-[state=active]:text-white">Fleet</TabsTrigger>
          <TabsTrigger value="services" className="rounded-xl h-11 px-6 font-head uppercase tracking-widest text-xs data-[state=active]:bg-[var(--lt-accent)] data-[state=active]:text-white">Services</TabsTrigger>
          <TabsTrigger value="areas" className="rounded-xl h-11 px-6 font-head uppercase tracking-widest text-xs data-[state=active]:bg-[var(--lt-accent)] data-[state=active]:text-white">Service Areas</TabsTrigger>
        </TabsList>
        <TabsContent value="fleet" className="mt-6"><CrudBlock cfg={SETS.fleet} /></TabsContent>
        <TabsContent value="services" className="mt-6"><CrudBlock cfg={SETS.services} /></TabsContent>
        <TabsContent value="areas" className="mt-6"><CrudBlock cfg={SETS.areas} /></TabsContent>
      </Tabs>
    </div>
  );
}
